<template>
  <div class="property-verification">
    <div class="verification-header text-center mb-4">
      <h4 class="text-primary">Verify Your Identity</h4>
      <p class="text-muted">Confirm your identity to proceed with property updates</p>
    </div>

    <!-- Error Alert -->
    <div v-if="errorMessage" class="alert alert-danger" role="alert">
      <i class="fas fa-exclamation-triangle mr-2"></i>
      <strong>Verification Failed:</strong> {{ errorMessage }}
      <button type="button" class="close" @click="errorMessage = ''" aria-label="Close">
        <span aria-hidden="true">&times;</span>
      </button>
    </div>

    <!-- Property Information Display -->
    <div class="property-info mb-4">
      <div class="card bg-light">
        <div class="card-header">
          <h6 class="mb-0">
            <i class="fas fa-home text-primary mr-2"></i>
            Selected Property Information
          </h6>
        </div>
        <div class="card-body">
          <div class="row">
            <div class="col-md-6">
              <p class="mb-2"><strong>LR Number:</strong> {{ property.LRNumber }}</p>
              <p class="mb-2"><strong>Owner:</strong> {{ property.ownerNames }}</p>
              <p class="mb-2"><strong>Location:</strong> {{ property.percelAddress }}</p>
            </div>
            <div class="col-md-6">
              <p class="mb-2"><strong>Sub-County:</strong> {{ property.subCountyName }}</p>
              <p class="mb-2"><strong>Ward:</strong> {{ property.wardName }}</p>
              <p class="mb-2"><strong>Size:</strong> {{ property.percelSize }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Identity Verification Form -->
    <form @submit.prevent="verifyIdentity" class="verification-form">
      <div class="form-section">
        <h6 class="section-title">
          <i class="fas fa-shield-alt text-info mr-2"></i>
          Identity Verification
        </h6>
        <p class="text-muted small mb-4">
          Please provide your details to verify that you are authorized to update this property's information.
        </p>

        <div class="row">
          <div class="col-md-6">
            <div class="form-group">
              <label for="fullName" class="form-label">
                <i class="fas fa-user"></i> Full Name *
              </label>
              <input
                  type="text"
                  class="form-control"
                  id="fullName"
                  v-model="verificationForm.fullName"
                  placeholder="Enter your full name as it appears on your ID"
                  required
                  :disabled="loading"
              >
              <small class="form-text text-muted">
                Must match the name on your identification document
              </small>
            </div>
          </div>
          <div class="col-md-6">
            <div class="form-group">
              <label for="idNumber" class="form-label">
                <i class="fas fa-id-card"></i> ID/Passport Number *
              </label>
              <input
                  type="text"
                  class="form-control"
                  id="idNumber"
                  v-model="verificationForm.idNumber"
                  placeholder="Enter your ID or Passport number"
                  required
                  :disabled="loading"
              >
              <small class="form-text text-muted">
                National ID or Passport number
              </small>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-md-6">
            <div class="form-group">
              <label for="phoneNumber" class="form-label">
                <i class="fas fa-phone"></i> Phone Number *
              </label>
              <input
                  type="tel"
                  class="form-control"
                  id="phoneNumber"
                  v-model="verificationForm.phoneNumber"
                  placeholder="0700000000"
                  required
                  :disabled="loading"
              >
              <small class="form-text text-muted">
                We'll use this to contact you if needed
              </small>
            </div>
          </div>
          <div class="col-md-6">
            <div class="form-group">
              <label for="email" class="form-label">
                <i class="fas fa-envelope"></i> Email Address (Optional)
              </label>
              <input
                  type="email"
                  class="form-control"
                  id="email"
                  v-model="verificationForm.email"
                  placeholder="your.email@example.com"
                  :disabled="loading"
              >
              <small class="form-text text-muted">
                For important updates about your request
              </small>
            </div>
          </div>
        </div>

        <div class="form-group">
          <label for="relationship" class="form-label">
            <i class="fas fa-users"></i> Relationship to Property *
          </label>
          <select
              class="form-control"
              id="relationship"
              v-model="verificationForm.relationship"
              required
              :disabled="loading"
          >
            <option value="">Select your relationship to this property</option>
            <option value="owner">Property Owner</option>
            <option value="co-owner">Co-owner</option>
            <option value="authorized-representative">Authorized Representative</option>
            <option value="family-member">Family Member</option>
            <option value="legal-guardian">Legal Guardian</option>
            <option value="power-of-attorney">Power of Attorney</option>
          </select>
          <small class="form-text text-muted">
            Your relationship to the property owner or the property itself
          </small>
        </div>

        <!-- Additional Authorization for Non-Owners -->
        <div v-if="verificationForm.relationship && verificationForm.relationship !== 'owner'" class="authorization-section">
          <div class="alert alert-info">
            <i class="fas fa-info-circle mr-2"></i>
            <strong>Additional Authorization Required:</strong> As you are not the primary property owner, you may need to provide additional documentation to verify your authorization to update this property's information.
          </div>

          <div class="form-group">
            <label for="authorizationDetails" class="form-label">
              Authorization Details *
            </label>
            <textarea
                class="form-control"
                id="authorizationDetails"
                v-model="verificationForm.authorizationDetails"
                rows="3"
                placeholder="Please explain your authorization to update this property (e.g., power of attorney document, family relationship, etc.)"
                required
                :disabled="loading"
            ></textarea>
          </div>
        </div>
      </div>

      <!-- Verification Actions -->
      <div class="verification-actions">
        <div class="row">
          <div class="col-md-6">
            <button
                type="button"
                class="btn btn-outline-secondary btn-block"
                @click="$emit('back-to-search')"
                :disabled="loading"
            >
              <i class="fas fa-arrow-left mr-2"></i>
              Back to Search
            </button>
          </div>
          <div class="col-md-6">
            <button
                type="submit"
                class="btn btn-primary btn-block"
                :disabled="loading || !isFormValid"
            >
              <span v-if="loading" class="spinner-border spinner-border-sm mr-2"></span>
              <i v-else class="fas fa-shield-alt mr-2"></i>
              {{ loading ? 'Verifying...' : 'Verify Identity' }}
            </button>
          </div>
        </div>
      </div>
    </form>

    <!-- Verification Guidelines -->
    <div class="verification-guidelines mt-4">
      <div class="card bg-light">
        <div class="card-body">
          <h6 class="card-title">
            <i class="fas fa-info-circle text-info mr-2"></i>
            Verification Guidelines
          </h6>
          <ul class="small mb-0">
            <li>Ensure all information matches your official identification documents</li>
            <li>Your phone number will be used for SMS verification if required</li>
            <li>Property owners have full update privileges</li>
            <li>Non-owners may require additional documentation for verification</li>
            <li>False information may result in rejection of your update request</li>
          </ul>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { execute, landrate } from '@/api'

export default {
  name: 'PropertyVerification',
  props: {
    property: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      loading: false,
      errorMessage: '',
      verificationForm: {
        fullName: '',
        idNumber: '',
        phoneNumber: '',
        email: '',
        relationship: '',
        authorizationDetails: ''
      }
    }
  },
  computed: {
    isFormValid() {
      const { fullName, idNumber, phoneNumber, relationship } = this.verificationForm
      const basicValid = fullName.trim() && idNumber.trim() && phoneNumber.trim() && relationship

      // If not owner, authorization details are required
      if (relationship && relationship !== 'owner') {
        return basicValid && this.verificationForm.authorizationDetails.trim()
      }

      return basicValid
    }
  },
  methods: {
    async verifyIdentity() {
      if (!this.isFormValid) {
        alert('Please fill in all required fields')
        return
      }

      this.loading = true

      try {
        // Prepare verification data
        const formData = new FormData()
        formData.append('function', 'verifyPropertyOwner')
        formData.append('propertyId', this.property.id || this.property.LRNumber)
        formData.append('fullName', this.verificationForm.fullName.trim())
        formData.append('idNumber', this.verificationForm.idNumber.trim())
        formData.append('phoneNumber', this.verificationForm.phoneNumber.trim())
        formData.append('email', this.verificationForm.email.trim())
        formData.append('relationship', this.verificationForm.relationship)
        formData.append('authorizationDetails', this.verificationForm.authorizationDetails.trim())

        // Call verification API
        const response = await execute(formData, landrate)

        if (response.data.success) {
          // Verification successful
          const verifiedOwnerData = {
            ...this.verificationForm,
            verificationId: response.data.data.verificationId || Date.now(),
            verifiedAt: new Date().toISOString(),
            verificationStatus: 'verified'
          }

          this.$emit('verification-success', verifiedOwnerData)
        } else {
          // Verification failed - show error message instead of going back
          this.showErrorMessage(response.data.message || 'Identity verification failed')
        }

      } catch (error) {
        console.error('Verification error:', error)
        this.showErrorMessage('Error during verification. Please try again.')
      } finally {
        this.loading = false
      }
    },

    showErrorMessage(message) {
      // Display error message to user
      this.errorMessage = message
      // Scroll to top to show error
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },

    formatPhoneNumber(value) {
      // Remove non-digits and format phone number
      return value.replace(/\D/g, '').substring(0, 10)
    }
  },

  watch: {
    'verificationForm.phoneNumber'(newVal) {
      this.verificationForm.phoneNumber = this.formatPhoneNumber(newVal)
    },
    'verificationForm.idNumber'(newVal) {
      this.verificationForm.idNumber = newVal.toUpperCase()
    }
  }
}
</script>

<style scoped>
.property-verification {
  max-width: 800px;
  margin: 0 auto;
}

.verification-header h4 {
  color: #2c3e50;
  font-weight: 600;
}

.property-info .card {
  border: 2px solid #e9ecef;
  border-radius: 10px;
}

.property-info .card-header {
  background: linear-gradient(45deg, #f8f9fa, #e9ecef);
  font-weight: 600;
}

.form-section {
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 10px;
  padding: 25px;
  margin-bottom: 20px;
}

.section-title {
  color: #495057;
  font-weight: 600;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 2px solid #e9ecef;
}

.form-label {
  font-weight: 600;
  color: #495057;
  margin-bottom: 5px;
}

.form-label i {
  color: #007bff;
  margin-right: 5px;
}

.form-control {
  border-radius: 6px;
  border: 2px solid #e9ecef;
  padding: 10px 12px;
  transition: all 0.3s ease;
}

.form-control:focus {
  border-color: #007bff;
  box-shadow: 0 0 0 0.2rem rgba(0,123,255,.25);
}

.authorization-section {
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #dee2e6;
}

.verification-actions {
  margin-top: 30px;
  padding-top: 20px;
  border-top: 2px solid #e9ecef;
}

.btn {
  border-radius: 6px;
  padding: 12px 20px;
  font-weight: 600;
  transition: all 0.3s ease;
}

.btn:hover:not(:disabled) {
  transform: translateY(-2px);
}

.btn-primary {
  background: linear-gradient(45deg, #007bff, #0056b3);
  border: none;
}

.btn-primary:hover:not(:disabled) {
  box-shadow: 0 5px 15px rgba(0,123,255,0.4);
}

.verification-guidelines .card {
  border: none;
  border-radius: 8px;
}

.verification-guidelines .card-title {
  color: #495057;
  font-weight: 600;
  margin-bottom: 10px;
}

.verification-guidelines ul {
  padding-left: 20px;
}

.verification-guidelines li {
  margin-bottom: 5px;
  color: #6c757d;
}

.alert {
  border-radius: 6px;
  border-left: 4px solid #17a2b8;
}

/* Responsive Design */
@media (max-width: 768px) {
  .form-section {
    padding: 20px;
  }

  .verification-actions .col-md-6 {
    margin-bottom: 10px;
  }
}
</style>